import { useEffect, useMemo, useState } from "react";

const BASE_URL = process.env.REACT_APP_URL_INFO;

const cache = new Map();
const inflight = new Map();

const DEFAULT_TTL_MS = 6 * 60 * 60 * 1000;

function now() {
  return Date.now();
}

function getCached(indicatorKey) {
  const hit = cache.get(indicatorKey);
  if (!hit) return null;
  if (hit.expiresAt <= now()) {
    cache.delete(indicatorKey);
    return null;
  }
  return hit.value;
}

function setCached(indicatorKey, value, ttlMs = DEFAULT_TTL_MS) {
  cache.set(indicatorKey, { value, expiresAt: now() + ttlMs });
}

async function fetchYearRange(indicatorKey) {
  const cached = getCached(indicatorKey);
  if (cached) return cached;

  if (inflight.has(indicatorKey)) {
    return await inflight.get(indicatorKey);
  }

  const p = (async () => {
    try {
      if (!BASE_URL) {
        throw new Error("REACT_APP_URL_INFO is not defined");
      }

      const res = await fetch(
        `${BASE_URL}/indicator-data/year-range?indicator=${encodeURIComponent(indicatorKey)}`,
        { headers: { Accept: "application/json" } }
      );

      if (!res.ok) return null;

      const json = await res.json();
      const min = json?.data?.oldestYear;
      const max = json?.data?.latestYear;

      if (typeof min !== "number" || typeof max !== "number") return null;

      const value = [min, max];
      setCached(indicatorKey, value);
      return value;
    } finally {
      inflight.delete(indicatorKey);
    }
  })();

  inflight.set(indicatorKey, p);
  return await p;
}

export default function useIndicatorYearRanges(indicatorKeys = [], options = {}) {
  const { ttlMs = DEFAULT_TTL_MS } = options;

  const [yearRanges, setYearRanges] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const normalizedKeys = useMemo(() => {
    if (!Array.isArray(indicatorKeys)) return [];
    const clean = indicatorKeys
      .map((k) => String(k ?? "").trim())
      .filter(Boolean);
    return Array.from(new Set(clean)).sort();
  }, [indicatorKeys]);

  const indicatorKeysKey = useMemo(
    () => normalizedKeys.join("|"),
    [normalizedKeys]
  );

  useEffect(() => {
    if (!indicatorKeysKey) return;

    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);

      try {
        const initial = {};
        const toFetch = [];

        for (const k of normalizedKeys) {
          const hit = getCached(k);
          if (hit) initial[k] = hit;
          else toFetch.push(k);
        }

        if (!cancelled) setYearRanges(initial);

        if (toFetch.length === 0) return;

        const results = await Promise.all(
          toFetch.map(async (k) => {
            const value = await fetchYearRange(k);
            if (value) setCached(k, value, ttlMs);
            return [k, value];
          })
        );

        if (cancelled) return;

        setYearRanges((prev) => {
          const next = { ...prev };
          for (const [k, value] of results) {
            if (value) next[k] = value;
          }
          return next;
        });
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [indicatorKeysKey, ttlMs, normalizedKeys]);

  return { yearRanges, loading, error };
}
